import { type QueryClient } from '@tanstack/react-query';

import { GALLERY_ASSETS, GALLERY_BOARDS, GALLERY_FILES } from '~/constants/react-query-keys';

const GALLERY_ITEM_MAIN_KEYS: string[] = [GALLERY_BOARDS, GALLERY_ASSETS, GALLERY_FILES];

export type InvalidateCollectionGalleryItemQueriesParams = {
  queryClient: QueryClient;
  collectionId: string;
};

/**
 * Invalidates every gallery item query family (boards / assets / files) rendering this
 * collection's contents, across BOTH cache key shapes in play:
 *
 * - the private collection page (`usePrivateShowcaseGalleryViewItems`) keys with
 *   `{ collectionId: '<uuid>', workspaceId, ... }`
 * - the collection link editor canvas and public collection page
 *   (`usePublicShowcaseGalleryViewItems`) key with `{ collectionId: true, shortId, ... }` — the
 *   LITERAL `true`, so a plain `[GALLERY_*, { collectionId }]` invalidation never matches it,
 *   which is how the editor canvas ended up frozen after adds (FOUND-477).
 *
 * The `collectionId: true` shape carries no collection uuid (only a shortId the collection item
 * mutations don't know), so that branch can't be scoped to one collection — every mounted
 * public/canvas collection gallery is invalidated. In practice only the surface the user is acting
 * on has one mounted, so the extra blast radius refetches nothing. A predicate (rather than
 * top-level `[GALLERY_*]` invalidation) keeps board-page and library galleries — which share these
 * main keys — untouched, e.g. when adding to a share link from a board page's share dropdown.
 */
export const invalidateCollectionGalleryItemQueries = ({
  queryClient,
  collectionId,
}: InvalidateCollectionGalleryItemQueriesParams) =>
  queryClient.invalidateQueries({
    predicate: ({ queryKey }) => {
      const [mainKey, keyParams] = queryKey;

      if (typeof mainKey !== 'string' || !GALLERY_ITEM_MAIN_KEYS.includes(mainKey)) {
        return false;
      }

      if (typeof keyParams !== 'object' || keyParams === null || !('collectionId' in keyParams)) {
        return false;
      }

      return keyParams.collectionId === collectionId || keyParams.collectionId === true;
    },
  });
